import React, { useState } from 'react';
import { Building2, User } from 'lucide-react';
import { FilterDropdown } from './ui/FilterDropdown';
import { Button } from './ui/Button';
import { MenuItemOptionsModal } from './MenuItemOptionsModal';
export function CreateOrderModal({
  companies,
  employees,
  menuItems,
  onClose,
  onSave
}: any) {
  const [formData, setFormData] = useState({
    employeeId: '',
    companyId: 0,
    branchId: '',
    menuItemId: '',
    quantity: 1,
    type: 'Envío'
  });
  // Custom date — defaults to today in YYYY-MM-DD
  const todayStr = new Date().toISOString().split('T')[0];
  const [customDate, setCustomDate] = useState(todayStr);
  // Toggle: menu selection vs manual entry
  const [manualMode, setManualMode] = useState(false);
  const [customProduct, setCustomProduct] = useState('');
  const [customAmount, setCustomAmount] = useState('');
  // Employee selection state
  const [employeeCompanyFilter, setEmployeeCompanyFilter] = useState('Todas');
  const [employeeSearch, setEmployeeSearch] = useState('');
  // Menu item options state
  const [showOptionsModal, setShowOptionsModal] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState<any[]>([]);
  const [optionsPrice, setOptionsPrice] = useState(0);
  // Filter only available menu items
  const availableMenuItems = menuItems.filter((item: any) => item.available !== false);
  const selectedCompany = companies.find((c: any) => c.id === formData.companyId);
  const selectedEmployee = employees.find((e: any) => e.id === formData.employeeId);
  const selectedMenuItem = availableMenuItems.find((m: any) => m.id === formData.menuItemId);
  const selectedBranch = selectedCompany?.branches.find((b: any) => b.id === formData.branchId);
  // Filtered employees by company + search
  const filteredEmployees = employees.filter((emp: any) => {
    const companyName = companies.find((c: any) => c.id === emp.company_id)?.name || '';
    const matchesCompany = employeeCompanyFilter === 'Todas' || companyName === employeeCompanyFilter;
    const searchLower = employeeSearch.toLowerCase();
    const matchesSearch = !employeeSearch || emp.name?.toLowerCase().includes(searchLower) || companyName.toLowerCase().includes(searchLower) || emp.username?.toLowerCase().includes(searchLower);
    return matchesCompany && matchesSearch;
  });
  // Company options for employee filter
  const employeeCompanyOptions = ['Todas', ...companies.map((c: any) => c.name)];
  const menuItemOptions = availableMenuItems.length > 0 ? ['Seleccionar plato', ...availableMenuItems.map((item: any) => `${item.name} - RD$ ${item.price.toFixed(2)}`)] : ['No hay platos disponibles'];
  const menuItemDisplayValue = selectedMenuItem ? `${selectedMenuItem.name} - RD$ ${selectedMenuItem.price.toFixed(2)}` : 'Seleccionar plato';
  // Calculate final price including options (menu mode)
  const itemPrice = selectedMenuItem ? selectedMenuItem.price + optionsPrice : 0;
  const isTaxExempt = selectedCompany?.taxExempt || false;
  const effectiveItemPrice = isTaxExempt ? itemPrice / 1.18 : itemPrice;
  const menuTotalPrice = effectiveItemPrice * formData.quantity;
  // Manual mode total
  const manualTotal = parseFloat(customAmount) || 0;
  // Effective total for display
  const displayTotal = manualMode ? manualTotal : menuTotalPrice;
  const canSubmit = manualMode ? !!(selectedEmployee && customProduct.trim() && manualTotal > 0) : !!(selectedEmployee && selectedMenuItem);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedEmployee || !selectedCompany || !selectedBranch) return;
    // Build custom ISO date from the date picker
    const customOrderDate = new Date(customDate + 'T12:00:00').toISOString();
    if (manualMode) {
      if (!customProduct.trim() || manualTotal <= 0) return;
      const orderData = {
        deliveryDate: customOrderDate,
        customOrderDate,
        type: formData.type,
        name: selectedEmployee.name,
        company: selectedCompany.name,
        branch: selectedBranch.name,
        product: customProduct.trim(),
        cashier: 'Admin',
        quantity: formData.quantity,
        catalogType: 'Almuerzo',
        subtotal: manualTotal.toFixed(2),
        discount: '0',
        total: manualTotal.toFixed(2),
        taxExempt: isTaxExempt,
        employeeId: selectedEmployee.id
      };
      onSave(orderData);
    } else {
      if (!selectedMenuItem) return;
      let productName = selectedMenuItem.name;
      if (selectedOptions.length > 0) {
        const optionNames = selectedOptions.map((o: any) => o.optionName).join(', ');
        productName = `${selectedMenuItem.name} (${optionNames})`;
      }
      const subtotal = effectiveItemPrice * formData.quantity;
      const orderData = {
        deliveryDate: customOrderDate,
        customOrderDate,
        type: formData.type,
        name: selectedEmployee.name,
        company: selectedCompany.name,
        branch: selectedBranch.name,
        product: productName,
        cashier: 'Admin',
        quantity: formData.quantity,
        catalogType: selectedMenuItem.catalogType || 'Almuerzo',
        subtotal: subtotal.toFixed(2),
        discount: '0',
        total: subtotal.toFixed(2),
        taxExempt: isTaxExempt,
        employeeId: selectedEmployee.id
      };
      onSave(orderData);
    }
  };
  const handleModalClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };
  const handleOptionsConfirm = (options: any[], totalWithOptions: number) => {
    setSelectedOptions(options);
    setOptionsPrice(totalWithOptions - (selectedMenuItem?.price || 0));
    setShowOptionsModal(false);
  };
  const handleMenuItemChange = (displayValue: string) => {
    const item = availableMenuItems.find((i: any) => `${i.name} - RD$ ${i.price.toFixed(2)}` === displayValue);
    setFormData({
      ...formData,
      menuItemId: item?.id || ''
    });
    setSelectedOptions([]);
    setOptionsPrice(0);
  };
  const handleSelectEmployee = (emp: any) => {
    setFormData({
      ...formData,
      employeeId: emp.id,
      companyId: emp.company_id || 0,
      branchId: emp.branch_id || '',
      menuItemId: ''
    });
    setSelectedOptions([]);
    setOptionsPrice(0);
  };
  const handleClearEmployee = () => {
    setFormData({
      ...formData,
      employeeId: '',
      companyId: 0,
      branchId: '',
      menuItemId: ''
    });
    setSelectedOptions([]);
    setOptionsPrice(0);
  };
  return <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={!showOptionsModal ? onClose : undefined} data-id="element-222">
        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-2xl border border-gray-200 dark:border-gray-800 max-h-[90vh] overflow-y-auto" onClick={handleModalClick} data-id="element-223">
          <div className="p-6 border-b border-gray-200 dark:border-gray-800" data-id="element-224">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-225">
              Crear Nueva Orden
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-226">
              Completa los datos para registrar un pedido
            </p>
          </div>

          <div className="p-6 space-y-5" data-id="element-227">
            {/* Fecha personalizada */}
            <div data-id="element-228">
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1" data-id="element-229">
                Fecha de la orden
              </label>
              <input type="date" value={customDate} onChange={e => setCustomDate(e.target.value)} className="w-full h-10 px-3 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/5 dark:focus:ring-white/5 focus:border-gray-900 dark:focus:border-white bg-white dark:bg-gray-800 text-sm font-medium text-gray-900 dark:text-white shadow-sm" data-id="element-230" />
            </div>

            {/* Employee Selection — custom searchable picker */}
            <div data-id="element-231">
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1" data-id="element-232">
                Empleado
              </label>

              {selectedEmployee /* Selected employee chip */ ? <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg" data-id="element-233">
                  <div className="flex items-center gap-3 min-w-0" data-id="element-234">
                    <div className="w-9 h-9 rounded-full bg-gray-900 dark:bg-white flex items-center justify-center text-white dark:text-gray-900 font-bold text-sm shrink-0" data-id="element-235">
                      {selectedEmployee.name?.charAt(0)?.toUpperCase()}
                    </div>
                    <div className="min-w-0" data-id="element-236">
                      <p className="text-sm font-semibold text-gray-900 dark:text-white truncate" data-id="element-237">
                        {selectedEmployee.name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate" data-id="element-238">
                        {companies.find((c: any) => c.id === selectedEmployee.company_id)?.name || 'Sin empresa'}
                        {selectedBranch?.name ? ` · ${selectedBranch.name}` : ''}
                      </p>
                    </div>
                  </div>
                  <button type="button" onClick={handleClearEmployee} className="text-xs text-gray-500 hover:text-red-600 dark:hover:text-red-400 font-medium px-2 py-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors shrink-0" data-id="element-239">
                    Cambiar
                  </button>
                </div> /* Employee search + filter + list */ : <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden" data-id="element-240">
                  {/* Filter bar */}
                  <div className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-100 dark:border-gray-700" data-id="element-241">
                    <div className="relative flex-1" data-id="element-242">
                      <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} data-id="element-243">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" data-id="element-244" />
                      </svg>
                      <input type="text" value={employeeSearch} onChange={e => setEmployeeSearch(e.target.value)} placeholder="Buscar empleado..." className="w-full h-8 pl-8 pr-3 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-900 dark:focus:ring-white text-gray-900 dark:text-white placeholder-gray-400" data-id="element-245" />
                    </div>
                    <select value={employeeCompanyFilter} onChange={e => setEmployeeCompanyFilter(e.target.value)} className="h-8 px-2 text-xs font-medium bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-1 focus:ring-gray-900 dark:focus:ring-white max-w-[160px]" data-id="element-246">
                      {employeeCompanyOptions.map((opt: string) => <option key={opt} value={opt} data-id="element-247">
                          {opt}
                        </option>)}
                    </select>
                  </div>

                  {/* Employee list */}
                  <div className="max-h-52 overflow-y-auto divide-y divide-gray-50 dark:divide-gray-800" data-id="element-248">
                    {filteredEmployees.length === 0 ? <div className="p-6 text-center" data-id="element-249">
                        <User className="w-8 h-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" data-id="element-250" />
                        <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-251">
                          {employeeSearch ? 'No se encontraron empleados' : 'No hay empleados disponibles'}
                        </p>
                      </div> : filteredEmployees.map((emp: any) => {
                  const companyName = companies.find((c: any) => c.id === emp.company_id)?.name || 'Sin empresa';
                  const company = companies.find((c: any) => c.id === emp.company_id);
                  const branch = company?.branches?.find((b: any) => b.id === emp.branch_id);
                  return <button key={emp.id} type="button" onClick={() => handleSelectEmployee(emp)} className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors" data-id="element-252">
                            <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-gray-600 dark:text-gray-300 font-bold text-xs shrink-0" data-id="element-253">
                              {emp.name?.charAt(0)?.toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0" data-id="element-254">
                              <p className="text-sm font-medium text-gray-900 dark:text-white truncate" data-id="element-255">
                                {emp.name}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400 truncate" data-id="element-256">
                                {companyName}
                                {branch?.name ? ` · ${branch.name}` : ''}
                              </p>
                            </div>
                            <Building2 className="w-3.5 h-3.5 text-gray-300 dark:text-gray-600 shrink-0" data-id="element-257" />
                          </button>;
                })}
                  </div>

                  {/* Count footer */}
                  <div className="px-3 py-1.5 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-100 dark:border-gray-700" data-id="element-258">
                    <p className="text-[10px] text-gray-400 dark:text-gray-500 font-medium" data-id="element-259">
                      {filteredEmployees.length} de {employees.length} empleados
                    </p>
                  </div>
                </div>}
            </div>

            {selectedEmployee && <>
                <div className="grid grid-cols-2 gap-4" data-id="element-260">
                  <div data-id="element-261">
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1" data-id="element-262">
                      Empresa
                    </label>
                    <div className="w-full h-10 px-3 flex items-center bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm font-medium text-gray-900 dark:text-white" data-id="element-263">
                      {selectedCompany?.name || '—'}
                    </div>
                  </div>
                  <div data-id="element-264">
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1" data-id="element-265">
                      Sucursal
                    </label>
                    <div className="w-full h-10 px-3 flex items-center bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm font-medium text-gray-900 dark:text-white" data-id="element-266">
                      {selectedBranch?.name || '—'}
                    </div>
                  </div>
                </div>

                {/* Toggle: Menú vs Manual */}
                <div className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700" data-id="element-267">
                  <button type="button" onClick={() => {
                setManualMode(false);
                setCustomProduct('');
                setCustomAmount('');
              }} className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${!manualMode ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm border border-gray-200 dark:border-gray-600' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'}`} data-id="element-268">
                    Seleccionar del Menú
                  </button>
                  <button type="button" onClick={() => {
                setManualMode(true);
                setFormData(f => ({
                  ...f,
                  menuItemId: ''
                }));
                setSelectedOptions([]);
                setOptionsPrice(0);
              }} className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${manualMode ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm border border-gray-200 dark:border-gray-600' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'}`} data-id="element-269">
                    Entrada Manual
                  </button>
                </div>

                {manualMode ? <>
                    {/* Manual: Product name */}
                    <div data-id="element-270">
                      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1" data-id="element-271">
                        Producto / Descripción
                      </label>
                      <input type="text" value={customProduct} onChange={e => setCustomProduct(e.target.value)} placeholder="Ej. Almuerzo especial, Plato del día..." className="w-full h-10 px-3 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/5 dark:focus:ring-white/5 focus:border-gray-900 dark:focus:border-white bg-white dark:bg-gray-800 text-sm font-medium text-gray-900 dark:text-white shadow-sm" data-id="element-272" />
                    </div>

                    {/* Manual: Amount + Quantity */}
                    <div className="grid grid-cols-2 gap-4" data-id="element-273">
                      <div data-id="element-274">
                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1" data-id="element-275">
                          Monto Total (RD$)
                        </label>
                        <div className="relative" data-id="element-276">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-bold text-gray-400" data-id="element-277">
                            RD$
                          </span>
                          <input type="number" step="0.01" min="0" value={customAmount} onChange={e => setCustomAmount(e.target.value)} placeholder="0.00" className="w-full h-10 pl-11 pr-3 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/5 dark:focus:ring-white/5 focus:border-gray-900 dark:focus:border-white bg-white dark:bg-gray-800 text-sm font-mono font-medium text-gray-900 dark:text-white shadow-sm" data-id="element-278" />
                        </div>
                      </div>
                      <div data-id="element-279">
                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1" data-id="element-280">
                          Cantidad
                        </label>
                        <input type="number" required min="1" value={formData.quantity} onChange={e => setFormData({
                    ...formData,
                    quantity: parseInt(e.target.value) || 1
                  })} className="w-full h-10 px-3 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/5 dark:focus:ring-white/5 focus:border-gray-900 dark:focus:border-white bg-white dark:bg-gray-800 text-sm font-medium text-gray-900 dark:text-white shadow-sm" data-id="element-281" />
                      </div>
                    </div>

                    <FilterDropdown label="Tipo" value={formData.type} options={['Envío', 'Retiro']} onChange={val => setFormData({
                ...formData,
                type: val
              })} className="w-full" data-id="element-282" />

                    {/* Manual summary */}
                    {customProduct && manualTotal > 0 && <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-xl border border-gray-100 dark:border-gray-800" data-id="element-283">
                        <h3 className="font-bold text-sm text-gray-900 dark:text-white mb-3" data-id="element-284">
                          Resumen
                        </h3>
                        <div className="space-y-1.5 text-sm" data-id="element-285">
                          <div className="flex justify-between" data-id="element-286">
                            <span className="text-gray-500 dark:text-gray-400" data-id="element-287">
                              Producto:
                            </span>
                            <span className="font-medium text-gray-900 dark:text-white" data-id="element-288">
                              {customProduct}
                            </span>
                          </div>
                          <div className="flex justify-between" data-id="element-289">
                            <span className="text-gray-500 dark:text-gray-400" data-id="element-290">
                              Fecha:
                            </span>
                            <span className="font-medium text-gray-900 dark:text-white" data-id="element-291">
                              {new Date(customDate + 'T12:00:00').toLocaleDateString('es-DO', {
                        weekday: 'short',
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric'
                      })}
                            </span>
                          </div>
                          {formData.quantity > 1 && <div className="flex justify-between" data-id="element-292">
                              <span className="text-gray-500 dark:text-gray-400" data-id="element-293">
                                Cantidad:
                              </span>
                              <span className="font-medium text-gray-900 dark:text-white" data-id="element-294">
                                ×{formData.quantity}
                              </span>
                            </div>}
                          <div className="flex justify-between pt-2 border-t border-gray-200 dark:border-gray-700" data-id="element-295">
                            <span className="font-bold text-gray-900 dark:text-white" data-id="element-296">
                              Total:
                            </span>
                            <span className="font-bold text-lg text-gray-900 dark:text-white" data-id="element-297">
                              RD$ {manualTotal.toFixed(2)}
                            </span>
                          </div>
                        </div>
                      </div>}
                  </> : <>
                    {/* Menu mode — existing behavior */}
                    <FilterDropdown label={availableMenuItems.length === 0 ? 'Plato (No hay platos disponibles)' : 'Plato'} value={menuItemDisplayValue} options={menuItemOptions} onChange={val => {
                if (val === 'Seleccionar plato' || val === 'No hay platos disponibles') {
                  setFormData({
                    ...formData,
                    menuItemId: ''
                  });
                  setSelectedOptions([]);
                  setOptionsPrice(0);
                } else {
                  handleMenuItemChange(val);
                }
              }} className="w-full" data-id="element-298" />

                    {selectedMenuItem && <div data-id="element-299">
                        <button type="button" onClick={e => {
                  e.stopPropagation();
                  setShowOptionsModal(true);
                }} className="w-full px-4 py-3 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 hover:border-gray-400 dark:hover:border-gray-500 transition-colors flex items-center justify-between" data-id="element-300">
                          <span data-id="element-301">
                            {selectedOptions.length > 0 ? `✅ ${selectedOptions.length} opción(es) seleccionada(s)` : '🍽️ Personalizar opciones del plato'}
                          </span>
                          <span className="text-gray-400" data-id="element-302">→</span>
                        </button>
                        {selectedOptions.length > 0 && <div className="mt-2 flex flex-wrap gap-1.5" data-id="element-303">
                            {selectedOptions.map((opt: any, idx: number) => <span key={idx} className="inline-flex items-center px-2 py-1 text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-md" data-id="element-304">
                                {opt.optionName}
                                {opt.priceModifier > 0 && <span className="ml-1 text-gray-500" data-id="element-305">
                                    +RD$ {opt.priceModifier.toFixed(2)}
                                  </span>}
                              </span>)}
                          </div>}
                      </div>}

                    <div className="grid grid-cols-2 gap-4" data-id="element-306">
                      <div data-id="element-307">
                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1" data-id="element-308">
                          Cantidad
                        </label>
                        <input type="number" required min="1" value={formData.quantity} onChange={e => setFormData({
                    ...formData,
                    quantity: parseInt(e.target.value) || 1
                  })} className="w-full h-10 px-3 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/5 dark:focus:ring-white/5 focus:border-gray-900 dark:focus:border-white bg-white dark:bg-gray-800 text-sm font-medium text-gray-900 dark:text-white shadow-sm" data-id="element-309" />
                      </div>

                      <FilterDropdown label="Tipo" value={formData.type} options={['Envío', 'Retiro']} onChange={val => setFormData({
                  ...formData,
                  type: val
                })} className="w-full" data-id="element-310" />
                    </div>

                    {selectedMenuItem && <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-xl border border-gray-100 dark:border-gray-800" data-id="element-311">
                        <h3 className="font-bold text-sm text-gray-900 dark:text-white mb-3" data-id="element-312">
                          Resumen
                        </h3>
                        <div className="space-y-1.5 text-sm" data-id="element-313">
                          <div className="flex justify-between" data-id="element-314">
                            <span className="text-gray-500 dark:text-gray-400" data-id="element-315">
                              Fecha:
                            </span>
                            <span className="font-medium text-gray-900 dark:text-white" data-id="element-316">
                              {new Date(customDate + 'T12:00:00').toLocaleDateString('es-DO', {
                        weekday: 'short',
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric'
                      })}
                            </span>
                          </div>
                          <div className="flex justify-between" data-id="element-317">
                            <span className="text-gray-500 dark:text-gray-400" data-id="element-318">
                              Precio base:
                            </span>
                            <span className="font-medium text-gray-900 dark:text-white" data-id="element-319">
                              RD$ {itemPrice.toFixed(2)}
                            </span>
                          </div>
                          {optionsPrice > 0 && <div className="flex justify-between" data-id="element-320">
                              <span className="text-gray-500 dark:text-gray-400" data-id="element-321">
                                Opciones:
                              </span>
                              <span className="font-medium text-gray-900 dark:text-white" data-id="element-322">
                                +RD$ {optionsPrice.toFixed(2)}
                              </span>
                            </div>}
                          {formData.quantity > 1 && <div className="flex justify-between" data-id="element-323">
                              <span className="text-gray-500 dark:text-gray-400" data-id="element-324">
                                Cantidad:
                              </span>
                              <span className="font-medium text-gray-900 dark:text-white" data-id="element-325">
                                ×{formData.quantity}
                              </span>
                            </div>}
                          <div className="flex justify-between" data-id="element-326">
                            <span className="text-gray-500 dark:text-gray-400" data-id="element-327">
                              ITBIS (18%):
                            </span>
                            <span className="font-medium text-gray-900 dark:text-white" data-id="element-328">
                              {isTaxExempt ? 'Exento' : `RD$ ${(itemPrice * formData.quantity - itemPrice * formData.quantity / 1.18).toFixed(2)}`}
                            </span>
                          </div>
                          {isTaxExempt && <div className="flex justify-between text-emerald-600 dark:text-emerald-400" data-id="element-329">
                              <span data-id="element-330">Descuento ITBIS:</span>
                              <span className="font-medium" data-id="element-331">
                                - RD${' '}
                                {(itemPrice * formData.quantity - menuTotalPrice).toFixed(2)}
                              </span>
                            </div>}
                          <div className="flex justify-between pt-2 border-t border-gray-200 dark:border-gray-700" data-id="element-332">
                            <span className="font-bold text-gray-900 dark:text-white" data-id="element-333">
                              Total:
                            </span>
                            <span className="font-bold text-lg text-gray-900 dark:text-white" data-id="element-334">
                              RD$ {menuTotalPrice.toFixed(2)}
                            </span>
                          </div>
                          {isTaxExempt ? <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1" data-id="element-335">
                              * Empresa exenta de ITBIS — se restó el 18%
                              incluido en el precio
                            </p> : <p className="text-xs text-gray-400 dark:text-gray-500 mt-1" data-id="element-336">
                              * El ITBIS ya está incluido en el precio del plato
                            </p>}
                        </div>
                      </div>}
                  </>}
              </>}

            <div className="flex gap-3 pt-2" data-id="element-337">
              <Button type="button" variant="outline" onClick={onClose} fullWidth data-id="element-338">
                Cancelar
              </Button>
              <Button type="button" onClick={handleSubmit} fullWidth disabled={!canSubmit} data-id="element-339">
                Crear Orden
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Menu Item Options Modal - OUTSIDE the backdrop div */}
      {selectedMenuItem && showOptionsModal && <MenuItemOptionsModal menuItem={selectedMenuItem} isOpen={showOptionsModal} onClose={() => setShowOptionsModal(false)} onConfirm={handleOptionsConfirm} data-id="element-340" />}
    </>;
}